import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { 
  Sun,
  Cloud,
  CloudRain,
  CloudSnow,
  CloudLightning,
  Wind,
  Eye,
  Tornado,
  Moon,
  CloudDrizzle,
  AlertTriangle,
  type LucideProps
} from 'lucide-react';

// Constants
const ICON_SIZE = 'h-[90px] w-[90px]';
export const DEFAULT_TEMPERATURE = 72;
export const MIN_TEMPERATURE = -50;
export const MAX_TEMPERATURE = 150;

// Type Definitions
const weatherConditions = [
  'hurricane', 'tornado', 'blizzard', 'dust-storm', 'clear-night', 'rain', 
  'light-rain', 'heavy-rain', 'hot', 'cloudy', 'sunny', 'windy', 'fog', 
  'storm', 'partly-cloudy', 'snow', 'default'
] as const;

type WeatherCondition = typeof weatherConditions[number];

export const WEATHER_ICONS: Record<WeatherCondition, React.ComponentType<LucideProps>> = {
  hurricane: Wind,
  tornado: Tornado,
  blizzard: CloudSnow,
  'dust-storm': AlertTriangle,
  'clear-night': Moon,
  rain: CloudRain,
  'light-rain': CloudDrizzle,
  'heavy-rain': CloudRain,
  hot: Sun,
  cloudy: Cloud,
  sunny: Sun,
  windy: Wind,
  fog: Eye,
  storm: CloudLightning,
  'partly-cloudy': Cloud,
  snow: CloudSnow,
  default: Sun,
};

export const WEATHER_THEMES: Record<WeatherCondition, { bg: string }> = {
  hurricane: { bg: 'bg-gradient-to-r from-teal-700 to-teal-600' },
  tornado: { bg: 'bg-gradient-to-r from-slate-600 to-slate-700' },
  blizzard: { bg: 'bg-gradient-to-r from-purple-600 to-purple-700' },
  'dust-storm': { bg: 'bg-gradient-to-r from-orange-600 to-orange-700' },
  'clear-night': { bg: 'bg-gradient-to-r from-purple-900 to-purple-950' },
  rain: { bg: 'bg-gradient-to-r from-gray-600 to-gray-700' },
  'light-rain': { bg: 'bg-gradient-to-r from-teal-400 to-teal-500' },
  hot: { bg: 'bg-gradient-to-r from-red-600 to-red-700' },
  cloudy: { bg: 'bg-gradient-to-r from-gray-500 to-gray-600' },
  sunny: { bg: 'bg-gradient-to-r from-blue-500 to-blue-600' },
  windy: { bg: 'bg-gradient-to-r from-indigo-400 to-indigo-500' },
  fog: { bg: 'bg-gradient-to-r from-gray-400 to-gray-500' },
  storm: { bg: 'bg-gradient-to-r from-purple-800 to-purple-900' },
  snow: { bg: 'bg-gradient-to-r from-violet-400 to-violet-500' },
  'heavy-rain': { bg: 'bg-gradient-to-r from-gray-700 to-gray-800' },
  'partly-cloudy': { bg: 'bg-gradient-to-r from-indigo-400 to-indigo-500' },
  default: { bg: 'bg-gradient-to-r from-blue-500 to-blue-600' },
};

interface WeatherClickData {
  temperature: number;
  condition: WeatherCondition;
  location: string;
  description?: string;
}

export interface WeatherWidgetProps {
  temperature?: number | string;
  condition?: WeatherCondition;
  location: string;
  description?: string;
  unit?: string;
  onClick?: (data: WeatherClickData) => void;
  className?: string;
}

export const WeatherWidget = React.forwardRef<HTMLButtonElement, WeatherWidgetProps>((
  {
    temperature = DEFAULT_TEMPERATURE,
    condition = 'sunny',
    location,
    description = '',
    unit = '°',
    onClick,
    className = '',
  }, ref) => {

  const parsedTemperature = typeof temperature === 'string' ? parseFloat(temperature) : temperature;
  const validatedTemperature = isNaN(parsedTemperature)
    ? DEFAULT_TEMPERATURE
    : Math.max(MIN_TEMPERATURE, Math.min(MAX_TEMPERATURE, parsedTemperature));

  const theme = WEATHER_THEMES[condition] || WEATHER_THEMES.default;
  const IconComponent = WEATHER_ICONS[condition] || WEATHER_ICONS.default;

  const handleClick = () => {
    if (onClick) {
      onClick({ temperature: validatedTemperature, condition, location, description });
    }
  };

  return (
    <Button
      ref={ref}
      variant="ghost"
      className={cn(
        'relative h-auto w-full overflow-hidden p-0 transition-all duration-300 hover:scale-105',
        'flex flex-row items-center justify-end gap-6 px-6',
        'rounded-3xl border border-white/20 text-white shadow-lg',
        theme.bg,
        className
      )}
      onClick={handleClick}
      aria-label={`Weather in ${location}: ${validatedTemperature}${unit}, ${condition}${description ? `, ${description}` : ''}`}
    >
      <div className="flex flex-row gap-3 items-center justify-start py-2.5 relative shrink-0">
        <div className="flex flex-col gap-1 items-start justify-start text-left leading-none">
          <div className="flex flex-row gap-1 items-center justify-start w-full text-nowrap">
            <div className="font-sans font-semibold text-[36px] leading-normal whitespace-pre">
              {validatedTemperature}{unit}
            </div>
            <div className="font-sans font-medium text-[20px] leading-[26px] whitespace-pre">
              {location}
            </div>
          </div>
          {description && (
            <div className="font-sans font-normal text-base leading-normal w-full">
              {description}
            </div>
          )}
        </div>
      </div>
      <div 
        className="absolute bg-center bg-cover bg-no-repeat size-[90px] -translate-x-1/2 -translate-y-1/2"
        style={{
          top: '50%',
          left: 'calc(50% - 131px)'
        }}
      >
        <IconComponent className={ICON_SIZE} />
      </div>
    </Button>
  );
});

WeatherWidget.displayName = 'WeatherWidget';

export default WeatherWidget;
